import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import styles from "./Checkbox.module.scss";
import clsx from "clsx";
import Label from "../Label/Label";

const CheckboxIcon = (props) => (
  <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 9" {...props}>
    <path
      d="M12 1 4.4 8 1 4.8"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Checkbox = (props, ref) => {
  const {
    checked,
    color,
    label,
    colorLabel,
    onChange,
    disabled = false,
    className,
    inputClassName,
    labelClassName,
    id,
    name,
    required,
    inverted = false,
  } = props;

  const containerClasses = clsx("d-flex", className);

  // this classes is for inverted checkbox for more info check here  https://www.figma.com/file/M09l3BP6rMMgt2s11FPIfO/Magic-Design-System-v.1.3.1-(Designership)?node-id=3%3A14
  const invertedClasses = {
    [styles.checkboxInverted]: inverted,
    [styles.checkedInverted]: checked && inverted,
    [styles.checkedPrimaryInverted]: checked && !disabled && color === "primary" && inverted,
  };

  const checkBoxClasses = clsx("mt-1", {
    [styles.checkbox]: !inverted,
    [styles.checked]: checked && !inverted,
    [styles.checkedPrimary]: checked && !disabled && color === "primary" && !inverted,
    [styles.checkedDanger]: checked && !disabled && color === "error" && !inverted,
    [styles.checkboxDisabled]: disabled,
    ...invertedClasses,
  });

  const checkboxInputClasses = clsx("d-none", inputClassName, {
    [styles.checkboxDisabled]: disabled,
  });

  const onChangeHandler = () => {
    if (!disabled && typeof onChange === "function") {
      onChange(!checked);
    }
  };

  const labelClasses = clsx("ml-3 font-weight-normal", styles.label, labelClassName);

  return (
    <div className={containerClasses} data-testid="Checkbox">
      <div className={checkBoxClasses} onClick={onChangeHandler} data-testid="CheckboxInput">
        <input
          ref={ref}
          className={checkboxInputClasses}
          type="checkbox"
          checked={checked}
          onChange={onChangeHandler}
          data-testid="CheckboxInputReal"
          name={name}
          id={id}
          disabled={disabled}
        />
        {checked ? <CheckboxIcon width={13} height={9} /> : null}
      </div>
      <Label
        className={labelClasses}
        onClick={onChangeHandler}
        dataTestId="CheckboxLabel"
        disabled={disabled}
        color={colorLabel}
        required={required}
        htmlFor={name}
        role="button"
      >
        {label}
      </Label>
    </div>
  );
};

const CheckboxWithRef = forwardRef(Checkbox);

CheckboxWithRef.defaultProps = {
  color: "primary",
  checked: false,
  disabled: false,
  required: false,
  name: "",
  inverted: false,
};

CheckboxWithRef.propTypes = {
  color: PropTypes.oneOf(["primary", "error"]),
  colorLabel: PropTypes.string,
  checked: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  labelClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  inputClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  required: PropTypes.bool,
};

export default CheckboxWithRef;
